/**
 * WebSocket client for the wire protocol. Owns the connection lifecycle:
 * Hello (the hub, or a floor room's join/reconnect), gameplay ops with an
 * auto-incrementing seq, typed dispatch of server packets, and auto-
 * reconnect with the slot token (a floor resume). The account credential is
 * ONE-SHOT: the server invalidates the account's session when the socket
 * closes (invalidate-on-disconnect), so a dropped FLOOR re-resumes its
 * frozen slot (slot token, no auth) and a dropped HUB re-logins — the host
 * (index.ts) shows the sign-in screen, since no credential of ours survives
 * a hub drop.
 */
import { ClientOp, ERR_ACCOUNT_IN_USE, ERR_AUTH_EXPIRED, ERR_NO_RECONNECT_SLOT, ERR_RECONNECT_BUSY, HUB_ROOM_ID, ServerOp, encodeClientPacket, retainRecoveryEvent } from "@pd/shared";
import type { ClientPacket, EquipSlot, InteractKind, Observation, PartyMemberInfo, PartyWire, InviteWire, PocketItem, ServerPacket, HubNpcWire, PlaytestLoadoutWire, PlaytestCatalogWire, RaceWire } from "@pd/shared";
import { traceItemClick, traceItemCommand } from "./item-click-debug.js";
import { readFloorResume, writeFloorResume } from "./floorResume.js";

export type RecoveryState = "live" | "stalled" | "reconnecting" | "syncing";
export type ClientTick = { tick: number; observation: Observation; events: unknown[]; resynced?: boolean; backlogTicks?: number };

/** Gameplay packets as the CLIENT sends them — seq is stamped by sendGameplay. */
export type GameplaySend =
  | { op: ClientOp.Move; x: number; y: number }
  | { op: ClientOp.Attack; targetId: number; targetIsEnemy: boolean }
  | {
      op: ClientOp.UseAbility;
      abilityId: number;
      targetId: number;
      x?: number;
      y?: number;
      targetKind?: "key" | "stone";
    }
  | { op: ClientOp.Interact; kind: InteractKind; targetId: number }
  | { op: ClientOp.PlaceGatestone; stoneId: number }
  | { op: ClientOp.TeleportGatestone; which: "personal" | "group" }
  | { op: ClientOp.Chat; message: string }
  | { op: ClientOp.GenerateGatestone }
  | { op: ClientOp.ReorderInventory; keys: number[]; carried: number[]; items: PocketItem[] }
  | { op: ClientOp.DropKey; keyId: number }
  | { op: ClientOp.EquipItem; itemId: number }
  | { op: ClientOp.UnequipItem; slot: EquipSlot }
  | { op: ClientOp.UseItem; slot: number }
  | { op: ClientOp.DropItem; slot: number }
  | { op: ClientOp.LeaveFloor };

export interface SessionInfo {
  playerId: number;
  /** HUB_ROOM_ID for the hub (the town — docs/design/hub.md), else the
   *  floor room's code. */
  roomId: string;
  /** Reconnect credential (floor rooms keep frozen slots). Absent for the
   *  hub: a dropped hub presence is gone, and rejoin is always fresh. */
  token?: string;
  /** The floor's party lead — -1 for the hub (no host there yet). */
  hostId: number;
  /** Floor rooms only — the hub is a fixed town with no floor size. */
  size?: string;
  /** Floor rooms only (the hub's capacity is a server knob). */
  capacity?: number;
  /** Party as the server knows it — each member may carry an account
   *  appearance (absent = the historical identity tint). For a hub Welcome
   *  this is the whole HUB roster (everyone in the town). */
  party: PartyMemberInfo[];
  /** The room's floor recipe id (floors.store — shared/floors.ts). Absent =
   *  a legacy all-NPC room, and always absent for the hub. */
  floor?: number;
  /** Present only in godmode (npm run dev -- --godmode) — the floor seed.
   *  Never present for a hub Welcome (there is no floor to seed). */
  seed?: number;
  /** The hub's party structure (a hub Welcome ONLY — hub.md): the town's
   *  parties (≥1 member in the town — a party of one is a party, A1) +
   *  pending invites, in hub member ids. A floor's Welcome has none: its
   *  party list is just its players. */
  parties?: PartyWire[];
  invites?: InviteWire[];
  /** Hub-only deterministic service NPCs (The Bull for the playtest). */
  npcs?: HubNpcWire[];
  /** Present for signed-in accounts in town. */
  playtestLoadout?: PlaytestLoadoutWire;
  playtestCatalog?: PlaytestCatalogWire;
}

export interface Handlers {
  onWelcome(info: SessionInfo): void;
  onRoomState(p: Extract<ServerPacket, { op: ServerOp.RoomState }>): void;
  /** The hub roster (docs/design/hub.md): the whole town, on every
   *  membership change. Identities + looks, never positions. */
  onHubState(p: Extract<ServerPacket, { op: ServerOp.HubState }>): void;
  /** A line of town chat (docs/design/hub.md): the sender's hub id + text.
   *  The hub has no fog — every member hears every line. */
  onHubChat(p: Extract<ServerPacket, { op: ServerOp.HubChat }>): void;
  /**
   * The race state (keyracing.md C1/D2): every open challenge and live race the
   * player can see, both rosters, each member's accept tick and stake, each
   * side's door state and outcome. Delivered to town AND floor sockets — the row
   * in the party panel is what tells a side mid-run that the other one is home.
   */
  onRaceState?(p: Extract<ServerPacket, { op: ServerOp.RaceState }>): void;
  /** One side forfeited (keyracing.md C3): the race id, the side, the reason.
   *  Server→client only — a client-asserted forfeit would be a claim about the
   *  other side (§5) — and delivered wherever the affected side's accounts are. */
  onRaceForfeit?(p: Extract<ServerPacket, { op: ServerOp.RaceForfeit }>): void;
  /**
   * One player's card (keyracing.md D1b), the answer to `inspectPlayer`. It is
   * NOT cached on the transport the way `RaceState` is: a card belongs to the one
   * menu that asked for it, and a scene switch that closes the menu closes the
   * question too. The consumer matches `card.id` to the presence it asked about —
   * hub ids are re-issued on every reconnect, so a late card can name somebody
   * else (traps/id-collision.md).
   */
  onPlayerCard?(p: Extract<ServerPacket, { op: ServerOp.PlayerCard }>): void;
  /** the Bull accepted a new account build. */
  onPlaytestLoadout(p: Extract<ServerPacket, { op: ServerOp.PlaytestLoadout }>): void;
  /** The run's rank (leaderboard.md): the floor's results screen shows it
   *  while still up — nulls on a retreat (no time to rank). */
  onRunRank(p: Extract<ServerPacket, { op: ServerOp.RunRank }>): void;
  onTick(p: ClientTick): void;
  onRecovery?(state: RecoveryState): void;
  onRecoveryEnded?(info: SessionInfo, tick: ClientTick, rank?: Extract<ServerPacket, { op: ServerOp.RunRank }>): void;
  onError(reason: string): void;
  onClosed(): void;
}

const RECONNECT_DELAYS = [500, 1500, 4000];

export class NetClient {
  private ws: WebSocket | null = null;
  private url: string;
  private handlers: Handlers;
  private seq = 0;
  private name = "";
  private hello: { roomId?: string; token?: string; size?: string; capacity?: number; seed?: number; floor?: number; auth?: string } | null = null;
  private deliberate = false;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  /** The account credential in `hello.auth` was rejected: stop, for good.
   *
   *  Without this the client loops. The server answers a dead token with
   *  Error(ERR_AUTH_EXPIRED) + close; the retry re-sends the SAME credential
   *  from `hello`, gets the same answer, and because the socket opens without
   *  trouble every time, the backoff never grows (see `reconnectAttempt` — it
   *  is reset by a Welcome, not by an open socket). The host's auth-expired
   *  handler then rebuilds the login screen on every bounce — a half-second
   *  render loop that wipes whatever the player is typing. A dead credential
   *  cannot be retried into life, so the first one ends the connection until
   *  the player signs in again (`connect()` clears this). */
  private authDead = false;
  private recovery: RecoveryState = "reconnecting";
  private roomId: string | null = null;
  private lastTick = -1;
  private lastReceivedAt = 0;
  private pendingTick: ClientTick | null = null;
  private frame: number | null = null;
  private watchdog: number | null = null;
  private syncRequest = 0;
  private waitingSync: number | null = null;
  private syncAt = 0;
  private resetNextTick = false;
  /** Read-only diagnostics for probes; contains no credential or map data. */
  readonly diagnostics = { state: "reconnecting" as RecoveryState, coalescedTicks: 0, lastTick: -1, syncRttMs: 0, serverBacklogTicks: 0 };

  get canPlay(): boolean { return this.recovery === "live" && this.ws?.readyState === WebSocket.OPEN; }

  /**
   * The last `RaceState` seen (keyracing.md D2). A welcome seeds it, a state
   * packet replaces it wholesale — the packet IS the state, and an absent race
   * is a dead one (revoked, expired, or resolved and dropped from the registry),
   * so merging would keep a window the server already closed on screen.
   *
   * It lives here rather than in a scene because the party panel is shared
   * chrome: the row must not go blank while a side is in its dungeon, which is
   * the moment its side's outcome may land for the OTHER side to see.
   */
  private races: RaceWire[] = [];
  /** When the cached race state arrived (`performance.now()`). The wire carries
   *  REMAINING durations and never a timestamp, so a countdown is anchored here —
   *  and anchored on the TRANSPORT rather than per scene, because walking through a
   *  door must not restart a clock that was already running down.
   *
   *  This cache — never the Welcome packet — is THE race state the scenes read. A
   *  floor's re-parent Welcome is written on the floor-ended tick and a side's
   *  forfeit is reported AFTER that (net/server.ts), so a Welcome handed to a scene
   *  can be exactly one packet stale; a town that preferred it kept showing a
   *  forfeited side as still racing (11-16). Whatever arrived last wins, always.
   */
  private raceArrival = 0;

  get raceState(): readonly RaceWire[] {
    return this.races;
  }

  get raceArrivedAt(): number {
    return this.raceArrival;
  }

  constructor(url: string, handlers: Handlers) {
    this.url = url;
    this.handlers = handlers;
    document.addEventListener("visibilitychange", this.onVisibility);
    document.addEventListener("resume", this.onVisibility);
    window.addEventListener("online", this.onOnline);
    window.addEventListener("pageshow", this.onVisibility);
  }

  restoreFloor(): boolean {
    const saved = readFloorResume();
    if (!saved) return false;
    this.roomId = saved.roomId;
    this.lastTick = saved.lastTick;
    this.connect(saved.name, { roomId: saved.roomId, token: saved.token });
    return true;
  }

  private setRecovery(state: RecoveryState): void {
    if (state === this.recovery) return;
    this.recovery = state;
    this.diagnostics.state = state;
    this.handlers.onRecovery?.(state);
  }

  private persistFloor(): void {
    const roomId = this.hello?.roomId;
    const token = this.hello?.token;
    writeFloorResume(roomId && token ? { roomId, token, name: this.name, lastTick: this.lastTick } : null);
  }

  private onVisibility = (): void => {
    if (document.hidden || this.deliberate || this.authDead) return;
    if (this.ws?.readyState === WebSocket.OPEN && this.roomId && this.roomId !== HUB_ROOM_ID) this.requestSync();
    else this.maybeReconnect();
  };

  private onOnline = (): void => {
    if (this.reconnectTimer !== null) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.onVisibility();
  };

  private requestSync(): void {
    if (this.waitingSync !== null || this.ws?.readyState !== WebSocket.OPEN) return;
    this.waitingSync = ++this.syncRequest;
    this.syncAt = performance.now();
    this.setRecovery("syncing");
    this.sendPacket({ op: ClientOp.Sync, requestId: this.waitingSync, lastTick: this.lastTick });
  }

  private checkConnection = (): void => {
    if (this.deliberate || this.authDead || !this.roomId || this.roomId === HUB_ROOM_ID) return;
    if (document.hidden) {
      // Keep a bounded latest observation while rendering sleeps. ACK at the
      // watchdog cadence, so background tabs do not flood or disconnect.
      if (this.pendingTick && this.ws?.readyState === WebSocket.OPEN)
        this.sendPacket({ op: ClientOp.Ack, roomId: this.roomId, tick: this.pendingTick.tick });
      return;
    }
    const age = performance.now() - this.lastReceivedAt;
    if (this.waitingSync !== null) {
      if (performance.now() - this.syncAt > 5000) this.restartConnection();
    } else if (age > 2000 && this.ws?.readyState === WebSocket.OPEN) {
      this.setRecovery("stalled");
      this.requestSync();
    }
  };

  private restartConnection(): void {
    const old = this.ws;
    this.ws = null; // old callbacks cannot interfere with the replacement
    this.discardTick();
    this.waitingSync = null;
    this.setRecovery("reconnecting");
    this.handlers.onClosed();
    old?.close();
    this.maybeReconnect();
  }

  private discardTick(): void {
    if (this.frame !== null) window.cancelAnimationFrame(this.frame);
    this.frame = null;
    this.pendingTick = null;
  }

  private flushTick = (): void => {
    const p = this.pendingTick;
    this.discardTick();
    if (!p || p.tick < this.lastTick) return;
    this.detectSilence();
    p.resynced ||= this.resetNextTick;
    this.resetNextTick = false;
    this.lastTick = p.tick;
    this.diagnostics.lastTick = p.tick;
    if (p.backlogTicks !== undefined) this.diagnostics.serverBacklogTicks = p.backlogTicks;
    if (this.waitingSync === null) this.setRecovery("live");
    this.handlers.onTick(p);
    this.persistFloor();
    if (this.roomId && this.roomId !== HUB_ROOM_ID && this.ws?.readyState === WebSocket.OPEN)
      this.sendPacket({ op: ClientOp.Ack, roomId: this.roomId, tick: p.tick });
  };

  private queueTick(p: ClientTick): void {
    if (p.tick <= this.lastTick) return;
    // A blocked main thread can receive contiguous OLD socket messages before
    // its watchdog gets a turn. Detect the silence before updating receipt
    // time, and require a fresh barrier rather than unlocking on that FIFO.
    this.detectSilence();
    this.lastReceivedAt = performance.now();
    if (this.pendingTick) {
      this.diagnostics.coalescedTicks++;
      p = { ...p, events: [...this.pendingTick.events.filter(retainRecoveryEvent), ...p.events], resynced: p.resynced || this.pendingTick.resynced };
    }
    // Long-hidden tabs retain a bounded history; a wake requests the server's
    // recent history and current state before gameplay resumes.
    if (p.events.length > 256) p.events = p.events.slice(-128);
    if (p.tick - this.lastTick > 20) p.resynced = true;
    this.pendingTick = p;
    // Scene changes must be consumed before the following Welcome.
    if (p.events.some((e) => e && typeof e === "object" && "type" in e && (e.type === "floor-ended" || e.type === "player-left"))) this.flushTick();
    else if (this.frame === null) this.frame = window.requestAnimationFrame(this.flushTick);
  }

  private detectSilence(): void {
    if (this.lastTick >= 0 && !document.hidden && performance.now() - this.lastReceivedAt > 2000)
      this.requestSync();
  }

  /** Connect with a Hello. `extra` = { roomId?, token?, size?, capacity?, seed?, floor?, auth? }.
   *  `auth` = the account session token (Hello.auth); the server overrides
   *  the name with the account's username and binds the saved look. */
  connect(
    name: string,
    extra: { roomId?: string; token?: string; size?: string; capacity?: number; seed?: number; floor?: number; auth?: string } = {},
  ): void {
    this.name = name;
    this.hello = extra;
    this.deliberate = false;
    this.authDead = false; // a fresh credential (sign-in) earns a fresh try
    this.reconnectAttempt = 0;
    if (this.reconnectTimer !== null) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.discardTick();
    this.waitingSync = null;
    this.setRecovery("reconnecting");
    if (this.watchdog === null) this.watchdog = window.setInterval(this.checkConnection, 500);
    this.open();
  }

  private open(): void {
    const previous = this.ws;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    previous?.close();
    // NOTE: a socket OPENING is not a healthy connection — the server accepts
    // the TCP/WS upgrade and then rejects the Hello (dead credential, closed
    // floor). Resetting the backoff here made every such rejection a fresh
    // 500 ms retry forever; `reconnectAttempt` is reset on Welcome instead
    // (dispatch below), the packet that proves the session is real.
    ws.onopen = () => {
      if (this.ws !== ws) return;
      traceItemClick("socket-open");
      this.send({ op: ClientOp.Hello, name: this.name, ...(this.hello ?? {}), recovery: true, lastTick: this.lastTick });
    };
    ws.onmessage = (ev) => { if (this.ws === ws) this.dispatch(ev.data as string); };
    ws.onclose = (event) => {
      if (this.ws !== ws) return; // a newer socket took over
      if (this.deliberate) return;
      traceItemClick("socket-close", { code: event.code, reason: event.reason });
      this.discardTick();
      this.waitingSync = null;
      this.setRecovery("reconnecting");
      this.handlers.onClosed();
      this.maybeReconnect();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private maybeReconnect(): void {
    if (this.deliberate || this.authDead || document.hidden || this.reconnectTimer !== null) return;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;
    // A FLOOR drop (hello.roomId set) re-resumes the frozen slot — the slot
    // token is the credential there, not the account's (one-shot, dead).
    // A HUB or pre-Hello drop does NOT auto-reconnect: the server removed
    // the town presence and released the account's session
    // (invalidate-on-disconnect), so no credential of ours survives. The
    // host (index.ts's onClosed) shows the sign-in screen instead.
    if (this.hello?.roomId === undefined) return;
    const delay = RECONNECT_DELAYS[this.reconnectAttempt] ?? 8000;
    this.reconnectAttempt++;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  /** Close deliberately: no auto-reconnect. */
  close(): void {
    this.deliberate = true;
    writeFloorResume(null);
    this.discardTick();
    if (this.watchdog !== null) clearInterval(this.watchdog);
    this.watchdog = null;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
  }

  /** Send a gameplay op, stamping the next seq. */
  sendGameplay(packet: GameplaySend): void {
    this.detectSilence(); // input may run before either socket/frame/watchdog work
    if (!this.canPlay || (this.ws?.bufferedAmount ?? 0) > 64 * 1024) {
      traceItemCommand({ ...packet, seq: this.seq } as ClientPacket, this.ws?.readyState);
      return;
    }
    const stamped = { ...packet, seq: this.seq++ } as ClientPacket;
    traceItemCommand(stamped, this.ws?.readyState);
    try { this.sendPacket(stamped); }
    catch (error) {
      traceItemClick("command-send-error", { op: stamped.op, error: String(error) });
      throw error;
    }
  }

  /** Report where MY hero stands in the town (ClientOp.HubMove). No seq, no
   *  authority: the server clamps and relays it to the rest of the town, and
   *  nothing in a run can see it (hub.md). Dropped silently while the socket
   *  is not open — a queued position would only arrive stale. */
  hubMove(x: number, y: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.HubMove, x, y });
  }

  /** Open the dungeon door (ClientOp.OpenDoor — pre-game, no seq). The
   *  server picks the floor's seed at that instant (hub.md's seed rule: the
   *  packet carries floor type + size and NOTHING else) and re-parents this
   *  socket's PARTY into the new floor (the server resolves the party; the
   *  packet does not name it). THE DOOR REQUIRES A PARTY
   *  (docs/design/keyracing.md A1): a party-less knock comes back as an Error,
   *  so call partyCreate() first — a solo is a party of one. There is no
   *  receipt: the answer is a floor Welcome on this same socket (index.ts's
   *  enterFloor switches the scene), or an Error the hub scene shows. */
  openDoor(floor: number, size: "small" | "medium" | "large"): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.OpenDoor, floor, size });
  }

  // ---- the party verbs (hub.md — pre-game, no seq, hub-only) --------------
  // The server is the judge (it refuses floor sockets and unaccounted
  // presences, and re-validates the cap at accept time); the answer is a
  // rebroadcast roster (HubState with the new parties/invites) or an Error
  // the hub scene notes. Dropped silently while the socket is not open, like
  // the other pre-game sends — a queued party verb would only arrive stale.

  /** Group up: invite `target` (a hub member id from the roster) to the party
   *  you are already in. Since A1 an invite is an offer of a seat in something
   *  that exists — with no party of your own it comes back as an Error, so
   *  partyCreate() first. */
  partyInvite(target: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.PartyInvite, target });
  }

  /** Found a party of one (ClientOp.PartyCreate — keyracing.md A1). The only
   *  way a party comes into existence besides being invited into one, and the
   *  prerequisite for opening the door alone. The answer is a rebroadcast
   *  roster (or an Error, e.g. "you are already in a party"). */
  partyCreate(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.PartyCreate });
  }

  /** Accept the pending invite from `from` (the inviter's hub id). */
  partyAccept(from: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.PartyAccept, from });
  }

  /** Decline the pending invite from `from` (the inviter's hub id). */
  partyDecline(from: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.PartyDecline, from });
  }

  /** Leave your own party. */
  partyLeave(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.PartyLeave });
  }

  /** Transfer your party's lead to `target` (their hub member id). The
   *  server is the judge (only the current host's transfer is honored, and
   *  the target must be a party member); the answer is a rebroadcast
   *  roster or an Error the hub scene notes. */
  partyTransfer(target: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.PartyTransfer, target });
  }

  // ---- the race verbs (keyracing.md C1/C2 — pre-game, no seq, hub-only) ----
  // Same shape as the party verbs: the server is the judge of every clause
  // (who is a host, who is in a party, who already holds a live race, which
  // sizes are raceable) and the answer is a rebroadcast `RaceState` or an
  // Error. Nothing here names a floor, a size or a seed except the challenge
  // itself — `RaceBegin` names the RACE and nothing else, because a door a
  // client can parameterise is a door a client can shop at.

  /** Challenge a PARTY, through any one of its members (`target` is their hub
   *  member id). You must be the host of your own party. The terms are locked
   *  at issue and cannot be countered — a different offer is a new challenge.
   *  `size` is medium|large: a small floor is a 40-second floor, and a
   *  40-second race measures who clicked first. */
  raceChallenge(target: number, floor: number, size: "medium" | "large", rated: boolean): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.RaceChallenge, target, floor, size, rated });
  }

  /** Accept an open challenge (every member of BOTH parties must accept; the
   *  issuing host's issue already counted). The last acceptance ARMS the race —
   *  that instant draws the seed, snapshots both rosters and starts the 30-minute
   *  window in which a host must open the door. */
  raceAccept(race: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.RaceAccept, race });
  }

  /** Reject an open challenge. A HOST's reject kills the offer outright (no
   *  floor, no rows, no rating). A MEMBER has no reject verb on the wire — their
   *  opt-out is `partyLeave()`, which the client labels Reject because that is
   *  what it looks like from their chair (keyracing.md D2, finding 9). */
  raceReject(race: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.RaceReject, race });
  }

  /** Open THIS side's race door (C2). Names the race and nothing else: the
   *  floor, the size and the seed are the server's locked terms. There is no
   *  receipt — the answer is a floor Welcome on this socket, or an Error — and
   *  there is no forfeit verb at all: a client-asserted forfeit is a claim about
   *  the other side, and the start window already does it. */
  raceBegin(race: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.RaceBegin, race });
  }

  /** Say something in the town (ClientOp.HubChat — pre-game, no seq, hub-
   *  only). The server truncates to the floor's chat length and broadcasts
   *  the line to the whole town (hub.md). Dropped silently while the socket
   *  is not open, like the other pre-game sends — a queued line would only
   *  arrive stale. */
  hubChat(message: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.HubChat, message });
  }

  /** Ask what a town presence looks like on paper (ClientOp.InspectPlayer —
   *  keyracing.md D1b): look, saved build, fastest clears, rating. Hub-only, no
   *  seq, and there is deliberately NO receipt to wait for: the answer is the
   *  card, or silence when the player has left town — a menu that gets nothing
   *  simply never fills in, which is the honest rendering of "nobody is there". */
  inspectPlayer(id: number): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({ op: ClientOp.InspectPlayer, id });
  }

  /** Update the account's next-run class and worn gear through the Bull. */
  playtestLoadout(loadout: PlaytestLoadoutWire): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.sendPacket({
      op: ClientOp.PlaytestLoadout,
      classKind: loadout.classKind,
      level: loadout.level,
      equipment: loadout.equipment,
    });
  }

  /** Send a pre-game packet (Hello is sent via connect(); Start via here). */
  send(packet: ClientPacket): void {
    this.sendPacket(packet);
  }

  private sendPacket(p: ClientPacket): void {
    this.ws?.send(encodeClientPacket(p));
  }

  private dispatch(raw: string): void {
    let p: ServerPacket;
    try {
      p = JSON.parse(raw) as ServerPacket;
    } catch {
      return; // trusted server; ignore garbage defensively
    }
    switch (p.op) {
      case ServerOp.Welcome:
        this.flushTick();
        if (this.roomId !== p.roomId) { this.lastTick = -1; this.seq = 0; }
        this.roomId = p.roomId;
        this.seq = Math.max(this.seq, p.nextSeq ?? 0);
        this.lastReceivedAt = performance.now();
        this.waitingSync = null;
        this.resetNextTick = p.roomId !== HUB_ROOM_ID;
        this.setRecovery(p.roomId === HUB_ROOM_ID ? "live" : "syncing");
        traceItemClick("welcome", { roomId: p.roomId, playerId: p.playerId });
        // The session is real: only now is a reconnect considered recovered.
        this.reconnectAttempt = 0;
        // Adopt the resume identity (hub.md's re-parent): a FLOOR welcome
        // (roomId + token) means a later drop must resume THIS floor's
        // frozen slot, not fall back to the town; a HUB welcome (no token)
        // means a later drop re-logins — a hub identity is stale by design
        // (the server refuses a hub token), so it is never saved. Without
        // this, a mid-floor socket drop would re-Hello with the original
        // create-time fields and land in the town instead of picking the
        // run back up.
        //
        // `auth` is dropped here too: the account's session token is
        // ONE-SHOT. The server invalidates the session when this socket
        // closes (invalidate-on-disconnect), so re-sending it on a retry can
        // only produce ERR_AUTH_EXPIRED — and a retry that carries it to a
        // floor resume is refused before the slot token is ever read.
        this.hello = {
          ...(this.hello ?? {}),
          roomId: p.roomId === HUB_ROOM_ID ? undefined : p.roomId,
          token: p.token,
          auth: undefined,
        };
        this.persistFloor();
        // A hub Welcome carries the town's races; a FLOOR Welcome carries none,
        // and a race that is running must keep its row through the door — so a
        // welcome without the field leaves the cached state alone rather than
        // clearing it. (A hub Welcome always sends the field, empty or not.)
        if (p.races) {
          this.races = p.races;
          this.raceArrival = performance.now();
        }
        this.handlers.onWelcome({
          playerId: p.playerId,
          roomId: p.roomId,
          token: p.token,
          hostId: p.hostId,
          size: p.size,
          capacity: p.capacity,
          party: p.party,
          floor: p.floor, // the room's floor recipe (floors.store); absent = legacy all-NPC room
          seed: p.seed,
          parties: p.parties, // hub welcomes only (the town's party structure)
          invites: p.invites,
          npcs: p.npcs,
          playtestLoadout: p.playtestLoadout,
          playtestCatalog: p.playtestCatalog,
        });
        break;
      case ServerOp.RoomState:
        this.handlers.onRoomState(p);
        break;
      case ServerOp.HubState:
        this.handlers.onHubState(p);
        break;
      case ServerOp.HubChat:
        this.handlers.onHubChat(p);
        break;
      case ServerOp.RaceState:
        // Cached on the transport as well as dispatched: the keyrace row belongs
        // to the party panel, which is on screen in town AND on a floor, and a
        // scene switch must not blank a race that is still running.
        this.races = p.races;
        this.raceArrival = performance.now();
        this.handlers.onRaceState?.(p);
        break;
      case ServerOp.RaceForfeit:
        this.handlers.onRaceForfeit?.(p);
        break;
      case ServerOp.PlayerCard:
        this.handlers.onPlayerCard?.(p);
        break;
      case ServerOp.PlaytestLoadout:
        this.handlers.onPlaytestLoadout(p);
        break;
      case ServerOp.RunRank:
        this.handlers.onRunRank(p);
        break;
      case ServerOp.Tick:
        this.queueTick(p as ClientTick);
        break;
      case ServerOp.Snapshot:
        if (p.roomId !== this.roomId || (this.waitingSync !== null && p.requestId !== this.waitingSync)) break;
        this.flushTick();
        this.seq = Math.max(this.seq, p.nextSeq);
        if (p.requestId !== undefined) this.diagnostics.syncRttMs = performance.now() - this.syncAt;
        this.waitingSync = null;
        this.diagnostics.serverBacklogTicks = p.backlogTicks;
        this.pendingTick = {
          tick: p.tick, observation: p.observation as Observation, resynced: true,
          events: p.events.filter((e) => e && typeof e === "object" && "tick" in e && typeof e.tick === "number" && e.tick > this.lastTick),
        };
        this.lastReceivedAt = performance.now();
        this.flushTick();
        break;
      case ServerOp.FinishedFloor:
        this.deliberate = true;
        this.discardTick();
        this.waitingSync = null;
        this.hello = null;
        this.roomId = null;
        writeFloorResume(null);
        if (this.watchdog !== null) clearInterval(this.watchdog);
        this.watchdog = null;
        this.handlers.onRecoveryEnded?.(p.info, { tick: p.tick, observation: p.observation as Observation, events: [p.event], resynced: true }, p.rank);
        break;
      case ServerOp.Error:
        traceItemClick("server-error", { reason: p.reason });
        if (p.reason === ERR_RECONNECT_BUSY) break; // close handler retries the same slot
        // A reconnect the server can't resume — the floor room closed (the
        // floor ended, or the server restarted). Under invalidate-on-
        // disconnect the account session died with the socket that was
        // dropped, so the recovery is the sign-in screen (the host decides
        // via onError below), not a fresh anonymous Hello. The fallback
        // cannot loop: it forwards once and strips the resume identity, so
        // the close handler's maybeReconnect finds nothing to retry.
        if (p.reason === ERR_AUTH_EXPIRED || p.reason === ERR_ACCOUNT_IN_USE) {
          // Terminal (see authDead): forget the credential, cancel any pending
          // retry, and let the host show the login. Retrying is what turned
          // one expired session into an endless rebuild of that login.
          this.authDead = true;
          writeFloorResume(null);
          if (this.hello) this.hello = { ...this.hello, auth: undefined };
          if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          this.handlers.onError(p.reason);
          return;
        }
        if (p.reason === ERR_NO_RECONNECT_SLOT && this.hello?.token !== undefined) {
          // The floor is gone and the account session died with the dropped
          // socket (invalidate-on-disconnect): a fresh anonymous Hello would
          // strand the player in town without a look or an account. The host
          // (index.ts) shows the sign-in screen. Stripping roomId+token also
          // keeps the close handler from retrying a resume that can never
          // succeed.
          this.hello = { ...this.hello, roomId: undefined, token: undefined };
          writeFloorResume(null);
          this.handlers.onError(p.reason);
          return;
        }
        this.handlers.onError(p.reason);
        break;
    }
  }
}
